import React, { useEffect, useState, forwardRef, useMemo, useCallback } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes, parseSelectOptions, generateFormFields } from 'utils/formUtils';
import _ from 'lodash';
import {Form} from 'antd';

const JfrogXrayMappingFormV2 = forwardRef(({disableProjectFields = false, ...props}, ref) => {
  const [form] = Form.useForm();
  const [repos, setRepos] = useState(() => {
    if (props.record && props.record.projectRepos) {
      const { projectRepos, projectName } = props.record;
      return [{id: projectRepos, name: projectName}]
    }
    return [];
  });

  const [builds, setBuilds] = useState(() => {
    if (props.record && props.record.buildName) {
      const { buildName, buildRepo } = props.record;
      const buildNameArrays = buildName.split(',');
      const buildRepoArrays = buildRepo ? buildRepo.split(',') : null;
  
      return buildNameArrays.map((id, index) => ({
        id,
        name: id,
        otherProperties: buildRepoArrays
          ? {
              type: 'build',
              buildRepo: buildRepoArrays[index] || null
            }
          : null
      }));
    }
    return [];
  });
  
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
        fetchLatestArtifactReport: true,
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        configType: 'projects',
        projectList: [{ projectRepos: undefined }],
      },
    ];
  });

  const fetchProjects = useCallback((configType, params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...(configType && { type : configType }), ...params });
    promise.then((response) => {
      const { projects } = response;
      if(configType === 'builds') {
        setBuilds((prevBuilds) => _.uniqBy([...prevBuilds, ...projects], 'id'));
      } else {
        setRepos(projects);
      }
    }).catch(() => {
      message.error('Problem fetching projects');
    });
    return { promise, cancel };
  },[props.loginConfigId,props.toolName]);

  useEffect(() => {
    const { cancel } = fetchProjects('repository');

    return () => {
      cancel && cancel();
    };
  },[fetchProjects]);

  const fetchBuilds = useCallback((params) => {
    return fetchProjects("builds", params)
  }, [fetchProjects]);


  const getExtraFieldsData = useCallback((config) => {
    const { projectRepos, buildName, dateRange, fetchLatestArtifactReport, configType } = config;
    let extraFieldData = { configType };
    if(configType === 'builds') {
      const data = builds.reduce((acc, cv) => {
        const { id, name, otherProperties } = cv;
        const buildRepo = otherProperties?.buildRepo;
      
        if (buildName.includes(id)) {
          acc.buildId.push(id);
          acc.buildName.push(name);
          acc.buildRepo.push(buildRepo);
        }
        return acc;
      }, { buildId: [], buildName: [], buildRepo: [] });
      
      extraFieldData = {
        ...extraFieldData,
        buildName: data.buildName.join(),
        buildRepo: _.compact(data.buildRepo).join(),
      };
    } else {
      extraFieldData = {
        ...extraFieldData,
        projectRepos: projectRepos.join(),
        dateRange,
        fetchLatestArtifactReport
      }
    }
    return extraFieldData;
  },[builds]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { buildName, projectRepos,  fetchLatestArtifactReport} = config;
    return {
      ...(!_.isEmpty(buildName) && {buildName: buildName.split(',')}),
      ...(!_.isEmpty(projectRepos) && {projectRepos: projectRepos.split(',')}),
      fetchLatestArtifactReport:  fetchLatestArtifactReport === 'true' ? true : false,
      configType: buildName ? 'builds' : 'projects'
    }
  }, []);

  const projectListRender = useCallback((listField) => {
    const selectedType = form.getFieldsValue().configurations[listField.name]?.configType;
    const repositoriesFields = [{
      name: 'projectRepos',
      colSpan: 10,
      type: FieldTypes.MULTI_SELECT,
      label: 'Project Repositories',
      options: parseSelectOptions(repos),
      disabled: disableProjectFields,
      required:true,
      props: {
        autoFocus: true
      },
    },    
    {
      name: 'dateRange',
      colSpan: 10,
      type: FieldTypes.NUMBER,
      min: 1,
      disabled: disableProjectFields,
      rules: [{
        validator: (rule, value) => {
            if (value && value < 1) {
                  return Promise.reject("Days cannot be less than 1.");
                }
              return Promise.resolve();
          }
        }
      ],
      label: 'Historical Data Range (in Days)',
      help: 'This config specifies historical data range. If config is specified then data for the past specific days will be pulled,  else ArmorCode will pull all the data from Jfrog Xray.'
    },
    {
      name: 'fetchLatestArtifactReport',
      colSpan: 12,
      type: FieldTypes.BOOLEAN,
      label: 'Fetch Latest Artifact Report',
      help: 'Retrieves the scan report for the most recent artifact from each selected repository. If unchecked, ArmorCode will ingest reports for all artifacts.',
      inline: true
    }]

    const buildFields = [
      {
        name: 'buildName',
        colSpan: 10,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Build Name',
        props: {
          api: fetchBuilds,
          allowClear: true,
        },
        disabled: disableProjectFields,
        required: true,
      }
    ]
    const formFields = selectedType === 'builds' ? buildFields : repositoriesFields;
    return (<div className="m-b-sm">
      {generateFormFields(formFields, 10, listField)}
    </div>)
  },[fetchBuilds,repos, disableProjectFields, form]);

  const extraFields = useMemo(() =>
    [
      {
        name: "configType",
        type: FieldTypes.RADIO,
        options: [{ label: 'Repositories', value: 'projects' }, { label: 'Builds', value: 'builds' }],
        disabled: disableProjectFields
      },
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender
      }
    ],
    [projectListRender, disableProjectFields])


  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      title={`Map ${props.toolName} Repositories`}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props }
    />
  )
})

export default JfrogXrayMappingFormV2;